import type { PermissionKey } from '@/lib/permissions';
import type { Role } from '@/types/roles';
import type { WorklynxIconName } from '@/components/icons/iconData';

export interface SettingsNavigationItem {
  id: 'general' | 'shift-rules' | 'roster-email' | 'public-holidays' | 'permissions' | 'billing';
  label: string;
  path: string;
  icon: WorklynxIconName;
  permission?: PermissionKey;
}

const businessOwnerSettings: SettingsNavigationItem[] = [
  { id: 'general', label: 'General', path: '/bo/settings', icon: 'settings' },
  {
    id: 'shift-rules',
    label: 'Shift Rules',
    path: '/bo/settings/shift-rules',
    icon: 'sliders',
  },
  {
    id: 'roster-email',
    label: 'Email',
    path: '/bo/settings/roster-email',
    icon: 'mail',
  },
  {
    id: 'public-holidays',
    label: 'Public Holidays',
    path: '/bo/settings/public-holidays',
    icon: 'calendar',
  },
  { id: 'permissions', label: 'Permissions', path: '/bo/permissions', icon: 'roles' },
  // The organisation's WorkLynx plan. It keeps its /bo/billing address because Stripe's
  // checkout returns there and the trial-expired redirect sends people there.
  { id: 'billing', label: 'Billing', path: '/bo/billing', icon: 'billing' },
];

const businessManagerSettings: SettingsNavigationItem[] = [
  {
    id: 'roster-email',
    label: 'Email',
    path: '/bm/settings/roster-email',
    icon: 'mail',
    permission: 'roster_digest.manage',
  },
  {
    id: 'shift-rules',
    label: 'Shift Rules',
    path: '/bm/settings/shift-rules',
    icon: 'sliders',
    permission: 'shift_rules.manage',
  },
  {
    id: 'public-holidays',
    label: 'Public Holidays',
    path: '/bm/settings/public-holidays',
    icon: 'calendar',
    permission: 'public_holidays.manage',
  },
  {
    id: 'permissions',
    label: 'Permissions',
    path: '/bm/permissions',
    icon: 'roles',
    permission: 'permissions.manage',
  },
];

export function getSettingsNavigationItems(
  role: Role | null,
  hasPermission: (permission: PermissionKey) => boolean,
): SettingsNavigationItem[] {
  if (role === 'BO') return businessOwnerSettings;
  if (role !== 'BM') return [];

  return businessManagerSettings.filter(
    (item) => !item.permission || hasPermission(item.permission),
  );
}
